import { createSlice } from '@reduxjs/toolkit';

const serviceBookingSlice = createSlice({
  name: 'serviceBooking',
  initialState: {
    commonData: {
      location: '',
      address: '',
      latitude: null,
      longitude: null,
      area: '',
      city: '',
      state: '',
      country: '',
      eventDate: null,
      eventTime: null,
      eventType: '',
      whoseEvent: '',
      eventId: null,
       shouldProcessCartAfterLogin: false,
      eventName: '',
      showEventTabs: false,
      
      // Tent house data
      tentServiceIds: [],
      selectedTentItems: [], // Array of { id, name, quantity, price, ... }
      tentTotalQuantity: 0,
      
      // Catering data
      cateringCuisineType: null,
      cateringCategory: null,
      cateringMealType: null,
      cateringSelectedItems: [], // Array of selected food items
      cateringQuantity: 1, // Number of guests/plates
      cateringCuisineTypeName: '',
      cateringCategoryName: '',
      cateringMealTypeName: ''
    },
    serviceType: '', // 'catering', 'priest', 'tent'
    priestEventName: '',
    selectedProviders: [],
    currentStep: 1,
    isDialogOpen: false,
    isProviderDialogOpen: false,
    availableEvents: [],
    tentItems: [],
    cateringItems: [],
       activeSelection: 'serviceForm',
  },
  reducers: {
    setCommonData: (state, action) => {
      state.commonData = { ...state.commonData, ...action.payload };
    },
    setServiceType: (state, action) => {
      state.serviceType = action.payload;
    },
    showTentItemsSelection: (state) => {
      state.activeSelection = 'tentItems';
    },
      showCateringSelection: (state) => {
      state.activeSelection = 'cateringSelection';
    },
    showServiceForm: (state) => {
      state.activeSelection = 'serviceForm';
    },
    setPriestEventName: (state, action) => {
      state.priestEventName = action.payload;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setEventId: (state, action) => {
      state.commonData.eventId = action.payload.id;
      state.commonData.eventName = action.payload.name;
    },
    setAvailableEvents: (state, action) => {
      state.availableEvents = action.payload;
    },
    setShowEventTabs: (state, action) => {
      state.showEventTabs = action.payload;
    },
      setShouldProcessCartAfterLogin: (state, action) => {
      state.shouldProcessCartAfterLogin = action.payload;
    },
    
    // Tent house actions
    setTentItems: (state, action) => {
      state.tentItems = action.payload;
      state.commonData.selectedTentItems = action.payload;
      state.commonData.tentServiceIds = action.payload.map(item => item.id);
      // Calculate total tent quantity
      state.commonData.tentTotalQuantity = action.payload.reduce((total, item) => {
        return total + (item.quantity || 1);
      }, 0);
    },
    
    updateTentItemQuantity: (state, action) => {
      const { itemId, quantity } = action.payload;
      const itemIndex = state.commonData.selectedTentItems.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        state.commonData.selectedTentItems[itemIndex].quantity = Math.max(1, quantity);
        
        // Recalculate total tent quantity
        state.commonData.tentTotalQuantity = state.commonData.selectedTentItems.reduce((total, item) => {
          return total + (item.quantity || 1);
        }, 0);
      }
    },
    
    // Catering actions
    setCateringItems: (state, action) => {
      state.cateringItems = action.payload;
      state.commonData.cateringSelectedItems = action.payload;
    },
    
    setCateringDetails: (state, action) => {
      state.commonData.cateringCuisineType = action.payload.cuisineType;
      state.commonData.cateringCategory = action.payload.category;
      state.commonData.cateringMealType = action.payload.mealType;
      state.commonData.cateringSelectedItems = action.payload.selectedItems || [];
      state.commonData.cateringQuantity = action.payload.quantity || 1;
      
      // Store names for display
      state.commonData.cateringCuisineTypeName = action.payload.cuisineTypeName || '';
      state.commonData.cateringCategoryName = action.payload.categoryName || '';
      state.commonData.cateringMealTypeName = action.payload.mealTypeName || '';
    },
    
    updateCateringQuantity: (state, action) => {
      state.commonData.cateringQuantity = Math.max(1, action.payload);
    },
    
    // Provider cart actions
    addProviderToCart: (state, action) => {
      const newProvider = action.payload;
      
      // For catering service, allow only one provider in cart
      if (state.serviceType === 'catering') {
        // Remove any existing catering provider
        state.selectedProviders = state.selectedProviders.filter(
          provider => provider.serviceType !== 'catering'
        );
      }
      
      // For tent service, check if same provider already exists
      if (state.serviceType === 'tent') {
        const existingProviderIndex = state.selectedProviders.findIndex(
          provider => provider.id === newProvider.id && provider.serviceType === 'tent'
        );
        
        if (existingProviderIndex !== -1) {
          // Update existing tent provider
          state.selectedProviders[existingProviderIndex] = {
            ...state.selectedProviders[existingProviderIndex],
            ...newProvider,
            quantity: newProvider.quantity || state.selectedProviders[existingProviderIndex].quantity
          };
          return;
        }
      }
      
      // For priest service or new tent provider, add to cart
      const existingProvider = state.selectedProviders.find(
        provider => provider.id === newProvider.id && provider.serviceType === state.serviceType
      );
      
      if (!existingProvider) {
        state.selectedProviders.push({
          ...newProvider,
          quantity: newProvider.quantity || 1
        });
      }
    },
    
    removeProviderFromCart: (state, action) => {
      state.selectedProviders = state.selectedProviders.filter(
        provider => provider.id !== action.payload
      );
    },
    
    updateProviderQuantity: (state, action) => {
      const { providerId, quantity } = action.payload;
      const provider = state.selectedProviders.find(p => p.id === providerId);
      
      if (provider) {
        provider.quantity = Math.max(1, quantity);
        
        // Update common data quantities if needed
        if (provider.serviceType === 'tent') {
          // You might want to sync tent item quantities here if needed
        } else if (provider.serviceType === 'catering') {
          state.commonData.cateringQuantity = quantity;
        }
      }
    },
    
    clearSelectedProviders: (state) => {
      state.selectedProviders = [];
    },
    
    // Dialog management
    openServiceDialog: (state) => {
      state.isDialogOpen = true;
      state.currentStep = 1;
    },
    closeDialogForLogin: (state) => {
  state.isDialogOpen = false;
  state.currentStep = 1;

  // state.selectedProviders = [];
},
    closeServiceDialog: (state) => {
  state.isDialogOpen = false;
  state.currentStep = 1;
  state.activeSelection = 'serviceForm';
   state.serviceType = '';
  // Reset form data when closing dialog
  state.commonData = {
    ...state.commonData,
    location: '',
    address: '',
    latitude: null,
    longitude: null,
    area: '',
    city: '',
    state: '',
    country: '',
    eventDate: null,
    eventTime: null,
    eventType: '',
    whoseEvent: '',
    eventId: null,
    eventName: '',
    
    // Also reset tent and catering selections
    tentServiceIds: [],
    selectedTentItems: [],
    tentTotalQuantity: 0,
    
    cateringCuisineType: null,
    cateringCategory: null,
    cateringMealType: null,
    cateringSelectedItems: [],
    cateringQuantity: 1,
    cateringCuisineTypeName: '',
    cateringCategoryName: '',
    cateringMealTypeName: ''
  };
  
  // Reset other selections
  state.selectedProviders = [];
},
    
    openProviderDialog: (state) => {
      state.isProviderDialogOpen = true;
    },
    
    closeProviderDialog: (state) => {
      state.isProviderDialogOpen = false;
    },
    
    resetServiceBooking: (state) => {
      state.commonData = {
        location: '',
        address: '',
        latitude: null,
        longitude: null,
        area: '',
        city: '',
        state: '',
        country: '',
        eventDate: null,
        eventTime: null,
        eventType: '',
        whoseEvent: '',
        eventId: null,
        eventName: '',
        tentServiceIds: [], 
        selectedTentItems: [],
        tentTotalQuantity: 0,
        cateringCuisineType: null,
        cateringCategory: null,
        cateringMealType: null,
        cateringSelectedItems: [],
        cateringQuantity: 1,
        cateringCuisineTypeName: '',
        cateringCategoryName: '',
        cateringMealTypeName: ''
      };
      state.serviceType = '';
      state.priestEventName = '';
      state.selectedProviders = [];
      state.currentStep = 1;
      state.isDialogOpen = false;
      state.isProviderDialogOpen = false;
      state.availableEvents = [];
      state.tentItems = [];
      state.cateringItems = [];
      state.shouldProcessCartAfterLogin = false;
    }
  }
});

export const {
  setCommonData,
  setServiceType,
  setPriestEventName,
  setCurrentStep,
  setEventId,
  setAvailableEvents,
  setTentItems,
  updateTentItemQuantity,
  setCateringItems,
  setCateringDetails,
  updateCateringQuantity,
  addProviderToCart,
  removeProviderFromCart,
  updateProviderQuantity,
  clearSelectedProviders,
  openServiceDialog,
  closeServiceDialog,
  openProviderDialog,
  closeProviderDialog,
  resetServiceBooking,
  //added new exports for now
  showTentItemsSelection,
  showCateringSelection,
  closeDialogForLogin,
  showServiceForm,
  setShowEventTabs,
  setShouldProcessCartAfterLogin,
} = serviceBookingSlice.actions;

export default serviceBookingSlice.reducer;